import type { FAQItem, FAQCategory } from './types';

export const nonPhysicianFAQ: FAQItem[] = [
  {
    id: 'non-physician-faq-eligibility-1',
    question: 'Who is eligible for EQuIP?',
    response:
      'Eligibility requires three criteria: (1) Full-time or part-time employment status, (2) Primary Care APC, Psychiatry APC, Palliative Medicine, or Teletriage specialty, and (3) Productivity-based pay (not salaried). All criteria must be met on Day 1 of the quarter and at the end of the quarter.',
    keyPoints: [
      'Must be full-time or part-time (not PRN)',
      'Specific specialties only: Primary Care APC, Psychiatry APC, Palliative Medicine, Teletriage',
      'Must be on productivity pay, not salary',
      'Criteria evaluated at start and end of quarter',
    ],
    category: 'eligibility',
  },
  {
    id: 'non-physician-faq-eligibility-2',
    question: 'What happens if I switch from productivity pay to salary mid-quarter?',
    response:
      'If you switch to salaried compensation during a quarter, you are not eligible for that quarter\'s incentive. Eligibility requires productivity-based pay at both the start and end of the quarter.',
    keyPoints: [
      'Switch to salary = ineligible for that quarter',
      'Must be on productivity pay for entire quarter',
      'No partial eligibility for pay type changes',
    ],
    category: 'eligibility',
  },
  {
    id: 'non-physician-faq-eligibility-3',
    question: 'What if I switch from full-time to part-time during the quarter?',
    response:
      'You remain eligible for an incentive, but it will be determined as a part-time award. Your average days worked (or hours for Teletriage) will be calculated using a rolling 90-day average from the date of your status change.',
    keyPoints: [
      'Still eligible, but for part-time award only',
      'Average days (or hours for Teletriage) calculated from status change date',
      'Uses rolling 90-day average',
    ],
    category: 'eligibility',
  },
  {
    id: 'non-physician-faq-eligibility-4',
    question: 'Am I eligible if I\'m terminated before the quarter ends?',
    response:
      'No. If your employment ends before the quarter closes, you are not eligible for that quarter\'s incentive. However, if you are terminated after the quarter ends, you are still eligible for the payout.',
    keyPoints: [
      'Terminated before quarter end = not eligible',
      'Terminated after quarter end = eligible for payout',
      'Employment must continue through quarter close',
    ],
    category: 'eligibility',
  },
  {
    id: 'non-physician-faq-1',
    question: 'Why are these the measures being used?',
    response:
      'These measures reflect our company goals for 2026 and our focus on outcomes-centered healthcare. They also reflect what we can reliably measure today. We are being intentional about grounding the program in data we can stand behind, rather than inferred or subjective metrics. As our analytics mature, the measures will evolve.',
    keyPoints: [
      'Alignment with company goals and Outcomes-Centered Healthcare',
      'Fairness and measurability',
      'Future evolution is expected',
    ],
    category: 'measures',
  },
  {
    id: 'non-physician-faq-2',
    question: 'The 90% threshold feels too high.',
    response:
      'The 90% threshold is applied consistently across all measures and is aligned with external benchmarks used to evaluate performance. It is intentionally balanced across multiple performance areas, so not meeting one measure does not eliminate the entire incentive opportunity.',
    keyPoints: [
      'Consistency across measures',
      'Aligned with external benchmarks',
      'Diversification reduces risk',
      'Thresholds are not negotiated quarter to quarter',
    ],
    category: 'thresholds',
  },
  {
    id: 'non-physician-faq-3',
    question: "I did not meet one measure. Does that mean I'm not eligible for any incentive?",
    response:
      'No. The program is designed so that not meeting one measure does not eliminate the potential for an incentive. Outcome Measures and Consistency Measures are independent, and performance is diversified across multiple measures.',
    keyPoints: ['Independent components', 'Protection of overall performance', 'Quarter-by-quarter reset'],
    category: 'general',
  },
  {
    id: 'non-physician-faq-4',
    question: "I didn't realize I was off track until the quarter ended.",
    response:
      "Performance visibility is available during the quarter. The dashboard exists to support visibility during the quarter. Leaders are expected to encourage APCs to review their individual data early and consistently so there are no surprises at the end.",
    keyPoints: [
      'Shared responsibility for visibility',
      'Dashboards should be reviewed regularly',
    ],
    category: 'process',
  },
  {
    id: 'non-physician-faq-5',
    question: "This doesn't reflect the quality of care I provide.",
    response:
      'The incentive metrics are focused on specific outcomes that we can reliably measure. It is not intended to capture the full impact you have on your patients.',
    keyPoints: [
      'Focused on reliably measurable outcomes',
      'Does not capture full clinical impact',
      'Opportunity to adjust going forward',
    ],
    category: 'general',
  },
  {
    id: 'non-physician-faq-6',
    question: "Why can't there be exceptions?",
    response:
      'To ensure fairness and consistency, the incentive program is applied uniformly across the organization. Leaders will review incentive payouts to confirm accuracy, and any discrepancies will be corrected by our centralized team. Exceptions for individual circumstances are not permitted to ensure consistent application of the program.',
    keyPoints: [
      'Fairness through consistency',
      'Leaders should not promise exceptions',
      'HR manages exception review',
    ],
    category: 'exceptions',
  },
  {
    id: 'non-physician-faq-7',
    question: 'Why does G2211 coding affect EQuIP incentives?',
    response:
      'Accurate coding is what allows the care we deliver to be fully reflected. G2211 helps capture the complexity and longitudinal nature of the care being provided. When coding does not fully reflect that care, it directly impacts the funding available for the incentive pool.',
    keyPoints: [
      'Incentives are funded through measurable outcomes and accurate coding',
      'G2211 reflects care already being delivered — it does not change clinical practice',
      'The full care team plays a role in enabling incentive funding',
      'This is about sustainability of the program, not compliance enforcement',
    ],
    category: 'measures',
  },
  {
    id: 'non-physician-faq-8',
    question: 'Will this change again next quarter?',
    response:
      "The Consistency Measures are set for the year to provide stability. If changes are needed in the future, they'll be communicated clearly and in advance.",
    keyPoints: ['Predictability', 'Advance communication'],
    category: 'process',
  },
  {
    id: 'non-physician-faq-9',
    question: "What should I focus on if I didn't earn the full incentive?",
    response:
      'The data helps identify where to focus so outcomes — and incentives — improve next quarter. Each quarter is a reset and an opportunity to adjust.',
    keyPoints: [
      'Forward-looking framing',
      'Improvement over judgment',
      'Outcomes-centered mindset',
    ],
    category: 'general',
  },
  {
    id: 'non-physician-faq-10',
    question: 'When should questions be escalated?',
    response:
      'Questions related to program design, thresholds, or calculations should be directed to HR@eventuswh.com. Leaders focus should remain on supporting understanding and reinforcing outcomes-centered care.',
    keyPoints: [
      'Program design questions go to HR@eventuswh.com',
      'Leaders focus on support and understanding',
    ],
    category: 'process',
  },
];

export const nonPhysicianFAQCategories: { id: FAQCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'general', label: 'General' },
  { id: 'measures', label: 'Measures' },
  { id: 'thresholds', label: 'Thresholds' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'process', label: 'Process' },
];
